import type { PhotoAppEntity } from './photoAppEntity';
import type { ChildId } from './valueObjects/childId';
import type { ExpirationDate } from './valueObjects/expirationDate';
import type { ExtensionDays } from './valueObjects/extensionDays';
import type { LastModifiedDatetime } from './valueObjects/lastModifiedDatetime';
import type { ParentId } from './valueObjects/parentId';
import type { SalesGroupId } from './valueObjects/salesGroupId';
import type { SalesGroupName } from './valueObjects/salesGroupName';
import type { PhotoSalesJsonPub, PhotoSalesJsonPvt } from '@/types/domain/photo/entities/photoSalesEntity';

import { EntityPub } from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';
import { getDateString } from '@/utils/date';

/**
 * 写真販売プロパティ非公開
 */
export type PhotoSalesPropsPvt = {
  /** 販売グループID */
  salesGroupId: SalesGroupId,
  /** 販売グループ名 */
  salesGroupName: SalesGroupName,
  /** 期限 */
  expirationDate: ExpirationDate,
  /** 延長日数 */
  extensionDays?: ExtensionDays,
  /** 写真リスト */
  photos: PhotoAppEntity[],
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 最終更新日時 */
  lastModifiedDatetime: LastModifiedDatetime,
}

/**
 * 写真販売プロパティ公開
 */
export type PhotoSalesPropsPub = Record<string, never>

/**
 * 販売した写真グループのEntity.
 * このEntityが保護者に共有される.
 */
export class PhotoSalesEntity extends EntityPub<
  PhotoSalesPropsPvt,
  PhotoSalesJsonPvt,
  PhotoSalesPropsPub,
  PhotoSalesJsonPub
> {
  /**
   * 削除タイプ
   */
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  /**
   * 削除キー
   */
  get deleteKey(): string {
    // 期限日の次の日に消す
    return this.getFutureDate(this.expirationDate.value, 1).toJSON();
  }

  toJsonData(): PhotoSalesJsonPvt {
    return {
      salesGroupId: this.salesGroupId.value,
      salesGroupName: this.salesGroupName.value,
      expirationDate: getDateString(this.expirationDate.value),
      extensionDays: this.extensionDays?.value,
      photos: this.photos.map((v) => v.toJsonData()),
      parentId: this.parentId.value,
      childId: this.childId.value,
      lastModifiedDatetime: this.lastModifiedDatetime.value.toJSON(),
    };
  }

  toJsonPubData(): PhotoSalesJsonPub {
    return {};
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    return EntityName.WagakoNurseryPhotoPhotoSalesEntity;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.salesGroupId.value}#${this.parentChildId}`;
  }

  /**
   * サブキー
   */
  get subKey() {
    return `${getDateString(this.expirationDate.value)}`;
  }

  /**
   * サブキー2
   */
  get subKey2() {
    return `${this.parentChildId}`;
  }

  /**
   * 親子ID
   */
  get parentChildId(): string {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 販売グループID
   */
  get salesGroupId(): SalesGroupId {
    return this._value.salesGroupId;
  }

  /**
   * 販売グループID
   */
  set salesGroupId(salesGroupId: SalesGroupId) {
    this._value.salesGroupId = salesGroupId;
  }

  /**
   * 販売グループ名
   */
  get salesGroupName(): SalesGroupName {
    return this._value.salesGroupName;
  }

  /**
   * 販売グループ名
   */
  set salesGroupName(salesGroupName: SalesGroupName) {
    this._value.salesGroupName = salesGroupName;
  }

  /**
   * 期限
   */
  get expirationDate(): ExpirationDate {
    return this._value.expirationDate;
  }

  /**
   * 期限
   */
  set expirationDate(expirationDate: ExpirationDate) {
    this._value.expirationDate = expirationDate;
  }

  /**
   * 延長日数
   */
  get extensionDays(): ExtensionDays | undefined {
    return this._value.extensionDays;
  }

  /**
   * 延長日数
   */
  set extensionDays(extensionDays: ExtensionDays | undefined) {
    this._value.extensionDays = extensionDays;
  }

  /**
   * 最終更新日時
   */
  get lastModifiedDatetime(): LastModifiedDatetime {
    return this._value.lastModifiedDatetime;
  }

  /**
   * 最終更新日時
   */
  set lastModifiedDatetime(lastModifiedDatetime: LastModifiedDatetime) {
    this._value.lastModifiedDatetime = lastModifiedDatetime;
  }

  /**
   * 写真一覧
   */
  get photos(): PhotoAppEntity[] {
    return this._value.photos;
  }

  /**
   * 写真一覧
   */
  set photos(photos: PhotoAppEntity[]) {
    this._value.photos = photos;
  }
}
